import { Alert, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, Stack, TextField, Typography } from '@mui/material';
import { AddCircleOutlineRounded, LoginRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApiRequestError } from '../../apiClients/base/ApiBase';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import BusySpinner from '../../components/BusySpinner';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { SessionStorageItemIds } from '../../Enums';
import { ProducerCreateInvitation, ProducerInvitationClaim } from '../../models/producer';

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
type ClaimForm = { firstName: string; lastName: string; ein: string; addressLine1: string; addressLine2: string; city: string; region: string; postalCode: string; country: string };

const invitationError = (problem: unknown): string => {
    if (!(problem instanceof ApiRequestError))
        return (problem as Error)?.message || 'We could not finish this invitation. Re-open the invitation link and retry.';
    if (problem.status === 404) return 'This invitation is invalid. Ask the sender for a new invitation.';
    if (problem.status === 410) return 'This invitation expired. Ask the sender for a fresh invitation.';
    if (problem.status === 409) return 'This invitation was already used or revoked.';
    if (problem.status === 403) return 'Sign in with the email address that received this invitation.';
    return problem.message || 'We could not finish this invitation. Re-open the invitation link and retry.';
};

const ProducerInvitationPage = () => {
    const { isLoggedIn, accessInfo, startProducerInvitation, getFreshIdentityToken, reloadAfterProducerInvitationClaimed } = useAccessInfo();
    const history = useHistory();
    const location = useLocation();
    const fragment = useMemo(() => new URLSearchParams(location.hash.slice(1)), [location.hash]);
    const loggedIn = isLoggedIn();
    const [credentials] = useState(() => ({
        invitationId: new URLSearchParams(location.hash.slice(1)).get('invitationId') || sessionStorage.getItem(SessionStorageItemIds.ProducerInvitationId) || '',
        token: new URLSearchParams(location.hash.slice(1)).get('token') || sessionStorage.getItem(SessionStorageItemIds.ProducerInvitationToken) || '',
    }));
    const { invitationId, token } = credentials;
    const validCredentials = guidPattern.test(invitationId) && token.length >= 32 && token.length <= 200;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [invitation, setInvitation] = useState<ProducerInvitationClaim | null>(null);
    const [form, setForm] = useState<ClaimForm>({
        firstName: accessInfo?.given_name || '', lastName: accessInfo?.family_name || '',
        ein: '', addressLine1: '', addressLine2: '', city: '', region: '', postalCode: '', country: '',
    });

    useEffect(() => {
        const fragmentId = fragment.get('invitationId');
        const fragmentToken = fragment.get('token');
        if (fragmentId && guidPattern.test(fragmentId) && fragmentToken && fragmentToken.length >= 32 && fragmentToken.length <= 200) {
            sessionStorage.setItem(SessionStorageItemIds.ProducerInvitationId, fragmentId);
            sessionStorage.setItem(SessionStorageItemIds.ProducerInvitationToken, fragmentToken);
        }
        if (location.hash || location.search) history.replace('/ProducerPortal/Invitation');
    }, [history, fragment, location.hash, location.search]);

    useEffect(() => {
        if (!validCredentials || !loggedIn) return;
        const controller = new AbortController();
        setBusy(true);
        setError('');
        new ProducerPortalClient().GetProducerInvitation(invitationId, token, controller.signal)
            .then(value => {
                setInvitation(value);
                // The values remain only in this component's memory after identity-bound validation.
                sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationId);
                sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationToken);
                setForm(previous => ({ ...previous, firstName: previous.firstName || accessInfo?.given_name || '', lastName: previous.lastName || accessInfo?.family_name || '' }));
            })
            .catch(problem => { if ((problem as any)?.name !== 'AbortError') setError(invitationError(problem)); })
            .finally(() => { if (!controller.signal.aborted) setBusy(false); });
        return () => controller.abort();
    }, [accessInfo?.family_name, accessInfo?.given_name, invitationId, loggedIn, token, validCredentials]);

    const complete = async () => {
        if (!invitation || !form.firstName.trim() || !form.lastName.trim()) return;
        try {
            setBusy(true);
            setError('');
            const payload: ProducerCreateInvitation = {
                InvitationId: invitationId,
                Token: token,
                IdToken: await getFreshIdentityToken(),
                FirstName: form.firstName.trim(),
                LastName: form.lastName.trim(),
                ...(invitation.InvitationKind === 'Team' ? {
                    ProducerCompanyEIN: form.ein.trim() || undefined,
                    MailingAddressLine1: form.addressLine1.trim() || undefined,
                    MailingAddressLine2: form.addressLine2.trim() || undefined,
                    MailingCity: form.city.trim() || undefined,
                    MailingRegion: form.region.trim() || undefined,
                    MailingPostalCode: form.postalCode.trim() || undefined,
                    MailingCountry: form.country.trim() || undefined,
                } : {}),
            };
            await new ProducerPortalClient().CompleteProducerInvitation(payload);
            const completed = await reloadAfterProducerInvitationClaimed(invitation.InvitationKind === 'Client' ? invitation.ClientOrganizationId || undefined : undefined);
            if (!completed) throw new Error('The invitation was accepted, but the new workspace could not be loaded. Refresh to retry.');
            history.replace(invitation.InvitationKind === 'Client' ? '/' : '/ProducerPortal');
        } catch (problem) {
            setError(invitationError(problem));
        } finally {
            setBusy(false);
        }
    };

    if (!validCredentials)
        return <Box sx={{ maxWidth: 680, margin: '32px auto', px: 2 }}><Alert severity="error">This producer invitation link is invalid.</Alert></Box>;

    if (!loggedIn)
        return <Box sx={{ maxWidth: 680, margin: '32px auto', px: 2 }}><Card variant="outlined">
            <CardHeader title="Accept invitation" subheader="Verify the identity that received this invitation." />
            <CardActions sx={{ px: 2, pb: 2 }}><Button variant="contained" startIcon={<LoginRounded />} onClick={() => startProducerInvitation(invitationId, token)}>Sign in to accept invitation</Button></CardActions>
        </Card></Box>;

    const isClientInvitation = invitation?.InvitationKind === 'Client';
    return <Box sx={{ maxWidth: 760, margin: '24px auto', px: 2 }}><BusySpinner busy={busy}><Card variant="outlined">
        <CardHeader title={isClientInvitation ? 'Join your organization' : 'Join producer company'} subheader={invitation?.ProducerCompanyName || 'Verifying invitation…'} />
        <Divider />
        <CardContent><Stack spacing={1.5}>
            {error && <Alert severity="error">{error}</Alert>}
            {invitation && <Alert severity="info">{isClientInvitation
                ? `This invitation grants organization Administrator access. After acceptance you will enter your normal organization workspace, not ${invitation.ProducerCompanyName}'s Producer portal.`
                : `This invitation grants ${invitation.ProducerRole} access to ${invitation.ProducerCompanyName}.`}</Alert>}
            <Typography variant="body2" color="text.secondary">Signed in as {accessInfo?.email || 'your verified account'}. The server verifies this address against the invitation recipient.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <TextField label="First name" value={form.firstName} onChange={event => setForm({ ...form, firstName: event.target.value })} fullWidth />
                <TextField label="Last name" value={form.lastName} onChange={event => setForm({ ...form, lastName: event.target.value })} fullWidth />
            </Stack>
            {invitation && !isClientInvitation && <>
                <Typography variant="subtitle2">Producer company details</Typography>
                <TextField label="Employer identification number (EIN)" value={form.ein} onChange={event => setForm({ ...form, ein: event.target.value })} />
                <TextField label="Mailing address line 1" value={form.addressLine1} onChange={event => setForm({ ...form, addressLine1: event.target.value })} />
                <TextField label="Mailing address line 2" value={form.addressLine2} onChange={event => setForm({ ...form, addressLine2: event.target.value })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField label="City" value={form.city} onChange={event => setForm({ ...form, city: event.target.value })} fullWidth />
                    <TextField label="State / region" value={form.region} onChange={event => setForm({ ...form, region: event.target.value })} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField label="Postal code" value={form.postalCode} onChange={event => setForm({ ...form, postalCode: event.target.value })} fullWidth />
                    <TextField label="Country" value={form.country} onChange={event => setForm({ ...form, country: event.target.value })} fullWidth />
                </Stack>
            </>}
        </Stack></CardContent>
        <CardActions sx={{ px: 2, pb: 2 }}><Button variant="contained" startIcon={<AddCircleOutlineRounded />} disabled={!invitation || !form.firstName.trim() || !form.lastName.trim() || busy} onClick={() => void complete()}>Accept invitation</Button></CardActions>
    </Card></BusySpinner></Box>;
};

export default ProducerInvitationPage;
